import { useState, useEffect, useCallback, useRef } from 'react';
import { Checkout } from '../models/Checkout';
import { useCheckoutApi } from '../../../../../../../../react-common/api/useCheckoutApi';
import { FallbackRequest } from '../models/FallbackRequest';
import { CheckoutFormData } from './../features/checkout-modal/CheckoutModal';
import { ICheckoutActions } from './../models/ICheckoutActions';
import { useAppContext } from '@app/react-common/AppContext';
import useSessionStorage from '@app/react-common/hooks/useSessionStorage';
import { Shippings } from '@app/pages/checkout-app/types';
import { CheckoutSettings } from '@pdqdev/pdq-shared/dist/entities';
import { ApiErrorNotification } from '@app/react-common/models/ApiErrorNotification';
import { CreateSegmentDto, ISegmentActions } from '../models/ISegmentActions';
import { ALL_GROUPS_ID } from '../features/CheckoutsView';
import { RuleEngineState } from '@app/react-common/components/rule-engine-v2/models/RuleEngineState';
import {
  mapRuleEngineApiDtoToRuleEngineState,
  mapRuleEngineStateToRuleEngineApiDto,
} from '@app/react-common/components/rule-engine-v2/utils/ruleEngineMapper';
import { TimeUnits } from '@utils/times-utils';
import { CheckoutGroupSettings } from '../models/CheckoutGroupSettings';
import { RateType } from '../constants/RateType';

const useCheckouts = () => {
  const [checkouts, setCheckouts] = useState<Checkout[]>([]);
  const [checkoutSettings, setCheckoutSettings] = useState<CheckoutSettings>(null);
  const [activeCheckoutId, setActiveCheckoutId] = useSessionStorage('pdq-active-checkout', null);
  const [activeCheckout, setActiveCheckout] = useState<Checkout>(null);
  const { showToast, showFullPageLoader } = useAppContext();
  const [apiErrorNotification, setApiErrorNotification] = useState<ApiErrorNotification>(null);
  const [isLoadingAllGroups, setIsLoadingAllGroups] = useState(false);
  const loadedGroupsRef = useRef<Set<number>>(new Set());

  const failureHandler = useCallback(async (response, status) => {
    if (status == 422) {
      setApiErrorNotification({ content: response.message, title: getErrorTitle(response.message) });
    } else {
      console.error('Response error:', response.message, status);
      showToast({ message: `An error has ocurred. Please contact support if the problem persists.`, mode: 'danger' });
    }
    showFullPageLoader(false);
  }, []);

  const checkoutApi = useCheckoutApi(failureHandler);

  const fetchData = async () => {
    showFullPageLoader(true);
    const response = await checkoutApi.get('/admin/v2/shippings?sortOrder=desc&limit=9999&includeShippings=default');
    let skipSegmentMapping = false;
    if (
      response.data.results.length > 0 &&
      response.data.results.some((x) =>
        x.checkoutShippingMethodGroupSegments.map((y) => y.rules).some((z) => Array.isArray(z)),
      )
    ) {
      showToast({
        message:
          'Segment rule maintenance in progress. If you want to use Segments, please check back in a few minutes.',
        mode: 'warning',
        duration: TimeUnits._SECOND_IN_MS * 10,
      });
      skipSegmentMapping = true;
    }

    const previouslyLoadedNonDefaultGroups = new Set<number>();
    checkouts.forEach((checkout) => {
      if (!checkout.isDefault && loadedGroupsRef.current.has(checkout.id)) {
        previouslyLoadedNonDefaultGroups.add(checkout.id);
      }
    });

    loadedGroupsRef.current.clear();

    setCheckouts(
      response.data.results.map((responseResult) => {
        let sortedSegments: any[] = [];
        if (!skipSegmentMapping) {
          sortedSegments = mapSegments(responseResult.checkoutShippingMethodGroupSegments, true);
        }

        if (responseResult.isDefault && responseResult.checkoutShippings?.length > 0) {
          loadedGroupsRef.current.add(responseResult.id);
        }

        const existingCheckout = checkouts.find((c) => c.id === responseResult.id);
        const shouldPreserveShippings = previouslyLoadedNonDefaultGroups.has(responseResult.id);

        return {
          ...responseResult,
          checkoutShippingMethodGroupSegments: sortedSegments,
          checkoutShippings:
            shouldPreserveShippings && existingCheckout?.checkoutShippings
              ? existingCheckout.checkoutShippings
              : responseResult.checkoutShippings || [],
        } as Checkout;
      }),
    );

    previouslyLoadedNonDefaultGroups.forEach((id) => loadedGroupsRef.current.add(id));

    showFullPageLoader(false);
  };

  const loadCheckoutSettings = async () => {
    const response = await checkoutApi.get('/admin/v1/settings');
    setCheckoutSettings(response.data);
  };

  const fetchCheckoutShippings = useCallback(
    async (groupId: number) => {
      if (loadedGroupsRef.current.has(groupId)) {
        return;
      }

      loadedGroupsRef.current.add(groupId);

      try {
        const response = await checkoutApi.get(`/admin/v2/shippings/${groupId}/methods`);

        setCheckouts((existingCheckouts) =>
          existingCheckouts.map((checkout) =>
            checkout.id === groupId ? { ...checkout, checkoutShippings: response.data || [] } : checkout,
          ),
        );
      } catch (error) {
        console.error(`Failed to load shippings for checkout group ${groupId}:`, error);
        loadedGroupsRef.current.delete(groupId);
        showToast({
          message: `Failed to load delivery methods. Please try again.`,
          mode: 'danger',
        });
      }
    },
    [checkoutApi, showToast],
  );

  const fetchAllCheckoutShippings = useCallback(async () => {
    const unloadedGroups = checkouts.filter((checkout) => !loadedGroupsRef.current.has(checkout.id));

    if (!unloadedGroups.length) {
      return;
    }

    setIsLoadingAllGroups(true);
    try {
      await Promise.all(unloadedGroups.map((group) => fetchCheckoutShippings(group.id)));
    } finally {
      setIsLoadingAllGroups(false);
    }
  }, [checkouts, fetchCheckoutShippings]);

  useEffect(() => {
    showFullPageLoader(true);
    Promise.all([fetchData(), loadCheckoutSettings()]).then(() => {
      showFullPageLoader(false);
    });
  }, []);

  useEffect(() => {
    if (!checkouts?.length) return;

    if (activeCheckoutId) {
      if (activeCheckoutId === ALL_GROUPS_ID) {
        setActiveCheckout(null);
        return;
      }
      const activeCheckout = checkouts.find((x) => x.id == activeCheckoutId);

      if (!activeCheckout) {
        setActiveCheckoutId(checkouts[0].id);
        return;
      }

      const needsShippings =
        !activeCheckout.isDefault &&
        (!activeCheckout.checkoutShippings || activeCheckout.checkoutShippings.length === 0);

      if (needsShippings) {
        fetchCheckoutShippings(activeCheckout.id);
      }

      setActiveCheckout(activeCheckout);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const activeId = urlParams.get('active');

    if (activeId) {
      setActiveCheckoutId(parseInt(activeId));
      return;
    }

    const defaultCheckout = checkouts.find((x) => x.isDefault) || checkouts[0];
    setActiveCheckoutId(defaultCheckout.id);
  }, [activeCheckoutId, checkouts]);

  const checkoutActions: ICheckoutActions = {
    createCheckout: async (checkout: CheckoutFormData) => {
      showFullPageLoader(true);
      const response = await checkoutApi.post('/admin/v1/shipping-methods-group', checkout);
      await fetchData();
      setActiveCheckoutId(response.data.id);
      showFullPageLoader(false);
      showToast({ message: 'Checkout created', mode: 'success' });
    },
    renameCheckout: async (id: number, checkout: CheckoutFormData) => {
      checkoutApi.patch('/admin/v1/shipping-methods-group', { id, ...checkout }).then((x) => {
        showToast({ message: 'Checkout renamed', mode: 'success' });
      });

      setCheckouts((existingCheckouts) => {
        existingCheckouts.find((x) => {
          if (x.id == id) {
            x.name = checkout.name;
            x.description = checkout.description;
            return Object.assign([], existingCheckouts);
          }
          return false;
        });
        return existingCheckouts;
      });

      setActiveCheckoutId(id);
    },
    deleteCheckout: async (id: number) => {
      checkoutApi.delete(`/admin/v1/shipping-methods-group/${id}`).then((x) => {
        showToast({ message: 'Checkout deleted', mode: 'success' });
      });
      setCheckouts((existingCheckouts) => {
        return existingCheckouts.filter((x) => x.id != id);
      });
      setActiveCheckoutId(checkouts[0].id);
    },
    toggleLiveCheckout: async (id: number, isLive: boolean) => {
      try {
        await checkoutApi.post(`/admin/v1/shipping-methods-group/${id}/set-live`, { enable: isLive });
        setCheckouts((existingCheckouts) =>
          existingCheckouts.map((x) => {
            if (x.id === id) {
              return { ...x, isLive };
            }
            return x;
          }),
        );
        showToast({ message: `Checkout ${isLive ? 'set as live' : 'disabled'}`, mode: 'success' });
      } catch (error) {
        return;
      }
    },
    setDefaultCheckout: async (id: number) => {
      const checkoutGroupName = checkouts.find((x) => x.id === id)?.name;
      showFullPageLoader(true);
      checkoutApi
        .post(`/admin/v1/shipping-methods-group/${id}/set-default`)
        .then(() => {
          showToast({ message: `${checkoutGroupName || 'Checkout'} set as default`, mode: 'success' });
        })
        .finally(() => {
          showFullPageLoader(false);
        });

      setCheckouts((existingCheckouts) => {
        return existingCheckouts.map((x) => {
          if (x.id === id) {
            return {
              ...x,
              isDefault: true,
              isLive: true,
            };
          } else if (x.isDefault) {
            return {
              ...x,
              isDefault: false,
              isLive: false,
            };
          }
          return x;
        });
      });
    },
    duplicateCheckout: async (id: number, checkout: CheckoutFormData) => {
      showFullPageLoader(true);
      const response = await checkoutApi.post(`/admin/v1/shipping-methods-group/${id}/duplicate`, checkout);
      await fetchData();
      showFullPageLoader(false);
      showToast({ message: 'Checkout duplicated', mode: 'success' });
      setActiveCheckoutId(response.data.id);
    },
    enableFallback: async (request: FallbackRequest): Promise<void> => {
      try {
        showFullPageLoader(true);
        if (!request.id) {
          const response = await checkoutApi.post(`/admin/v1/shippings`, {
            name: request.name,
            shippingMethodGroupId: request.checkoutId,
            rate: request.rate,
            fallbackMethod: true,
            enabled: request?.enabled ?? true,
            metadata: {
              isInternational: request?.isInternational ?? false,
              disableShippingAddressConditionCheck: request?.disablePOBoxCheck ?? false,
              fallbackRateType: request?.rateType ?? RateType.FIXED,
            },
          });
          setCheckouts((existingCheckouts) => {
            existingCheckouts.find((checkout) => {
              if (checkout.id == request.checkoutId) {
                const shipping = response.data as Shippings;
                shipping.metadata = {
                  isInternational: request?.isInternational ?? false,
                  disableShippingAddressConditionCheck: request?.disablePOBoxCheck ?? false,
                  fallbackRateType: request?.rateType ?? RateType.FIXED,
                };
                checkout.checkoutShippings.push(shipping);
                return Object.assign([], existingCheckouts);
              }
              return false;
            });

            return existingCheckouts;
          });
        } else {
          await checkoutApi.patch(`/admin/v1/shippings/${request.id}`, {
            name: request.name,
            rate: request.rate,
            enabled: request.enabled ?? true,
            metadata: {
              isInternational: request?.isInternational ?? false,
              disableShippingAddressConditionCheck: request?.disablePOBoxCheck ?? false,
              fallbackRateType: request?.rateType ?? RateType.FIXED,
            },
          });
          setCheckouts((existingCheckouts) => {
            existingCheckouts.find((x) => {
              const shipping = x.checkoutShippings.find((y) => y.id == request.id.toString());
              if (shipping != undefined) {
                shipping.enabled = request.enabled ?? true;
                shipping.rate = request.rate;
                shipping.name = request.name;
                if (!shipping.metadata) shipping.metadata = {};
                shipping.metadata.disableShippingAddressConditionCheck = request?.disablePOBoxCheck ?? false;
                shipping.metadata.isInternational = request?.isInternational ?? false;
                shipping.metadata.fallbackRateType = request?.rateType ?? RateType.FIXED;
                return Object.assign([], existingCheckouts);
              }
              return false;
            });
            return existingCheckouts;
          });
        }
      } catch (error) {
        console.error('Error enable fallback rate:', error);
        throw new Error('Failed to enable fallback rate');
      } finally {
        showFullPageLoader(false);
      }
    },
    disableFallback: async (fallbackMethodId): Promise<void> => {
      try {
        showFullPageLoader(true);
        await checkoutApi.patch(`/admin/v1/shippings/${fallbackMethodId}`, { enabled: false });
        setCheckouts((existingCheckouts) => {
          existingCheckouts.find((x) => {
            const checkout = x.checkoutShippings.find((y) => y.id == fallbackMethodId.toString());
            if (checkout != undefined) {
              checkout.enabled = false;
              return true;
            }
            return false;
          });

          return Object.assign([], existingCheckouts);
        });
      } catch (error) {
        console.error('Error disabling fallback rate:', error);
        throw new Error('Failed to disable fallback rate');
      } finally {
        showFullPageLoader(false);
      }
    },
    toggleFallbackConditionCheck: async (fallbackMethodId, disabled): Promise<void> => {
      checkoutApi
        .patch(`/admin/v1/shippings/${fallbackMethodId}`, {
          metadata: { disableShippingAddressConditionCheck: disabled },
        })
        .then((x) => {
          showToast({ message: `Changes saved`, mode: 'success' });
        });
      setCheckouts((existingCheckouts) => {
        existingCheckouts.find((x) => {
          const checkout = x.checkoutShippings.find((y) => parseInt(y.id) == fallbackMethodId);
          if (checkout != undefined) {
            if (!checkout.metadata) checkout.metadata = {};

            checkout.metadata.disableShippingAddressConditionCheck = disabled;
            return true;
          }
          return false;
        });

        return Object.assign([], existingCheckouts);
      });
    },
    toggleEnableDeliveryMethod: async (deliveryMethodId, enabled, onComplete): Promise<void> => {
      checkoutApi.patch(`/admin/v1/shippings/${deliveryMethodId}`, { enabled: enabled }).then((x) => {
        showToast({ message: `Delivery method ${enabled ? 'enabled' : 'disabled'}`, mode: 'info' });
        onComplete();
      });

      setCheckouts((existingCheckouts) => {
        for (let i = 0; i < existingCheckouts.length; i++) {
          for (let j = 0; j < existingCheckouts[i].checkoutShippings.length; j++) {
            if (parseInt(existingCheckouts[i].checkoutShippings[j].id) == deliveryMethodId) {
              existingCheckouts[i].checkoutShippings[j].enabled = enabled;
              return Object.assign([], existingCheckouts);
            }
          }
        }
        return existingCheckouts;
      });
    },
    deleteDeliveryMethod: async (id: number): Promise<void> => {
      checkoutApi.delete(`/admin/v1/shippings/${id}`).then((x) => {
        showToast({ message: 'Delivery method deleted', mode: 'success' });
      });
      setCheckouts((existingCheckouts) => {
        let deliveryMethod = null;
        const checkout = existingCheckouts.find((x) => {
          deliveryMethod = x.checkoutShippings.find((y) => y.id == id.toString());
          if (deliveryMethod != undefined) {
            return true;
          }
          return false;
        });
        checkout.checkoutShippings = checkout.checkoutShippings.filter((x) => x.id != deliveryMethod.id);

        return existingCheckouts;
      });
    },
    saveCheckoutMethodGroupSettings: async (
      shippingMethodGroupId: number,
      checkoutGroupSettings: CheckoutGroupSettings,
    ) => {
      return checkoutApi
        .patch(`/admin/v1/shipping-methods-group-settings`, {
          shippingMethodGroupId: shippingMethodGroupId,
          enableDisplayRatesOptimization:
            checkoutGroupSettings?.displayRatesOptimization?.enableDisplayRatesOptimization ?? false,
          enableIncreaseRatesPercentage:
            checkoutGroupSettings?.displayRatesOptimization?.enableIncreaseRatesPercentage ?? false,
          increaseRatesPercentage: checkoutGroupSettings?.displayRatesOptimization?.increaseRatesPercentage ?? 0,
          enableSameNameShippingMethodsKeepCheapestOnly:
            checkoutGroupSettings?.displayRatesOptimization?.enableSameNameShippingMethodsKeepCheapestOnly ?? false,
          deliveryMethodSortingMode: checkoutGroupSettings?.deliveryMethodSorting?.deliveryMethodSortingMode ?? null,
          deliveryMethodSortingPriorityMethod:
            checkoutGroupSettings?.deliveryMethodSorting?.deliveryMethodSortingPriorityMethod ?? null,
          nonBusinessDays: checkoutGroupSettings?.nonBusinessDays ?? null,
          categoryBasedShippingConfig: checkoutGroupSettings?.categoryBasedShippingConfig ?? null,
        })
        .then((x) => {
          showToast({ message: `Checkout Method group setting saved`, mode: 'info' });
        });
    },
    duplicateMethodToOtherCheckoutGroups: async (deliveryMethodId: number, checkoutGroupsIds: number[]) => {
      showFullPageLoader(true);
      await checkoutApi
        .post(`/admin/v1/shipping-methods-group/${deliveryMethodId}/duplicate-method-to-groups`, {
          checkoutGroupsIds: checkoutGroupsIds,
        })
        .then(() => {
          showToast({ message: 'Delivery method duplicated to selected checkouts', mode: 'success' });
        });
      await fetchData();
      showFullPageLoader(false);
    },
    updateCheckoutPriority: async (id: number, priority: number) => {
      await checkoutApi.patch('/admin/v1/shipping-methods-group', { id, priority });
    },
    saveDeliveryMethodDisplayOrder: async (checkoutGroupId, displayOrder) => {
      try {
        await checkoutApi.patch(
          `/admin/v1/shipping-methods-group/${checkoutGroupId}/delivery-method-display-order`,
          displayOrder,
        );
      } catch {
        return false;
      }
      const placementByMethodId = new Map(
        displayOrder.deliveryMethodPlacements.map((placement) => [placement.id.toString(), placement]),
      );
      setCheckouts((existingCheckouts) =>
        existingCheckouts.map((checkout) =>
          checkout.id === checkoutGroupId
            ? {
                ...checkout,
                deliveryMethodSections: displayOrder.deliveryMethodSections,
                checkoutShippings: checkout.checkoutShippings.map((shipping) => {
                  const placement = placementByMethodId.get(String(shipping.id));
                  return placement
                    ? {
                        ...shipping,
                        deliveryMethodSectionId: placement.deliveryMethodSectionId,
                        manualSortOrder: placement.manualSortOrder,
                      }
                    : shipping;
                }),
              }
            : checkout,
        ),
      );
      showToast({ message: 'Order saved', mode: 'success' });
      return true;
    },
  };

  const segmentActions: ISegmentActions = {
    createSegment: async (segment: CreateSegmentDto) => {
      setCheckouts((checkouts) => {
        return checkouts.map((x) => {
          if (x.id == segment.shippingMethodGroupId) {
            return {
              ...x,
              checkoutShippingMethodGroupSegments: [
                {
                  internalId:
                    x.checkoutShippingMethodGroupSegments && x.checkoutShippingMethodGroupSegments.length > 0
                      ? Math.max(...x.checkoutShippingMethodGroupSegments.map((segment) => segment.internalId)) + 1
                      : 0,
                  name: segment.name,
                  matchAllRules: segment.matchAllRules,
                  shippingMethodGroupId: segment.shippingMethodGroupId,
                  expanded: true,
                  rerender: true,
                  rules: segment.rules || { matchAllRuleGroups: true, ruleGroups: [] },
                  rulesBeforeEdit: segment.rules || { matchAllRuleGroups: true, ruleGroups: [] },
                },
                ...x.checkoutShippingMethodGroupSegments,
              ],
            };
          }
          return x;
        });
      });
    },
    removeSegment: async (checkoutId: number, internalId: number) => {
      setCheckouts((checkouts) => {
        return checkouts.map((x) => {
          if (x.id == checkoutId) {
            const newSegments = [];
            for (const segment of x.checkoutShippingMethodGroupSegments) {
              if (segment.internalId !== internalId) {
                newSegments.push(segment);
              } else {
                if (segment.id) {
                  checkoutApi.delete(`/admin/v1/shipping-methods-group/segment/${segment.id}`).then((response) => {
                    showToast({ message: 'Segment deleted', mode: 'success' });
                  });
                }
              }
            }
            return {
              ...x,
              checkoutShippingMethodGroupSegments: newSegments,
            };
          }
          return x;
        });
      });
    },
    expandSegment: async (checkoutId: number, internalId: number) => {
      setCheckouts((checkouts) => {
        return checkouts.map((x) => {
          if (x.id == checkoutId) {
            return {
              ...x,
              checkoutShippingMethodGroupSegments: x.checkoutShippingMethodGroupSegments.map((segment) => {
                if (segment.internalId == internalId) {
                  return {
                    ...segment,
                    expanded: !segment.expanded,
                  };
                }
                return segment;
              }),
            };
          }
          return x;
        });
      });
    },
    updateSegmentState: (checkoutId: number, internalId: number, ruleEngineState: RuleEngineState) => {
      setCheckouts((checkouts) => {
        return checkouts.map((x) => {
          if (x.id == checkoutId) {
            return {
              ...x,
              checkoutShippingMethodGroupSegments: x.checkoutShippingMethodGroupSegments.map((segment) => {
                if (segment.internalId == internalId) {
                  return {
                    ...segment,
                    rules: ruleEngineState,
                  };
                }
                return segment;
              }),
            };
          }
          return x;
        });
      });
    },
    saveSegmentToApi: async (checkoutId: number, internalId: number) => {
      let newSegmentId;
      const checkout = checkouts.find((x) => x.id == checkoutId);
      const segment = checkout.checkoutShippingMethodGroupSegments.find((segment) => segment.internalId === internalId);
      const segmentToSave = {
        id: segment.id,
        name: segment.name,
        matchAllRules: segment.matchAllRules,
        shippingMethodGroupId: segment.shippingMethodGroupId,
        rules: mapRuleEngineStateToRuleEngineApiDto(segment.rules),
      };
      if (!segment.id) {
        const response = await checkoutApi.post('/admin/v1/shipping-methods-group/segments', segmentToSave);
        newSegmentId = response.data.id;
        showToast({
          message: 'Changes saved.',
          mode: 'success',
        });
      } else {
        await checkoutApi.put('/admin/v1/shipping-methods-group/segments', segmentToSave);
        showToast({
          message: 'Changes saved.',
          mode: 'success',
        });
      }

      setCheckouts((checkouts) => {
        return checkouts.map((checkout) => {
          if (checkout.id == checkoutId) {
            return {
              ...checkout,
              checkoutShippingMethodGroupSegments: checkout.checkoutShippingMethodGroupSegments.map((segment) => {
                if (segment.internalId === internalId) {
                  return {
                    ...segment,
                    id: newSegmentId ?? segment.id,
                    rulesBeforeEdit: segment.rules,
                    expanded: false,
                    rerender: !segment.rerender,
                  };
                }
                return segment;
              }),
            };
          }
          return checkout;
        });
      });
    },
    updateSegmnetName: async (checkoutId: number, internalId: number, text: string) => {
      setCheckouts((checkouts) => {
        return checkouts.map((x) => {
          if (x.id == checkoutId) {
            return {
              ...x,
              checkoutShippingMethodGroupSegments: x.checkoutShippingMethodGroupSegments.map((segment) => {
                if (segment.internalId === internalId) {
                  if (segment.id) {
                    checkoutApi.put('/admin/v1/shipping-methods-group/segments', {
                      id: segment.id,
                      name: text,
                      matchAllRules: segment.matchAllRules,
                      shippingMethodGroupId: segment.shippingMethodGroupId,
                      rules: mapRuleEngineStateToRuleEngineApiDto(segment.rules),
                    });
                  }
                  return {
                    ...segment,
                    name: text,
                  };
                }
                return segment;
              }),
            };
          }
          return x;
        });
      });
    },
    cancelSegmentEdits: (checkoutId: number, internalId: number) => {
      setCheckouts((checkouts) => {
        return checkouts.map((x) => {
          if (x.id == checkoutId) {
            return {
              ...x,
              checkoutShippingMethodGroupSegments: x.checkoutShippingMethodGroupSegments.map((segment) => {
                if (segment.internalId === internalId) {
                  console.log('Cancelling edits', segment.rulesBeforeEdit, segment.rules);
                  return {
                    ...segment,
                    rules: Object.assign({}, segment.rulesBeforeEdit),
                    rerender: !segment.rerender,
                  };
                }
                return segment;
              }),
            };
          }
          return x;
        });
      });
    },
    checkIfSegmentHasBeenEdited: (checkoutId: number, internalId: number) => {
      const checkout = checkouts.find((x) => x.id == checkoutId);
      const segment = checkout?.checkoutShippingMethodGroupSegments?.find(
        (segment) => segment.internalId === internalId,
      );
      if (!segment) return true;

      const rulesHaveBeenEdited = JSON.stringify(segment.rules) !== JSON.stringify(segment.rulesBeforeEdit);
      return rulesHaveBeenEdited;
    },
  };

  const mapSegments = (segments, applySort) => {
    return groupSegmentsByAbTestId(segments, applySort).map((segment, segmentidx) => ({
      ...segment,
      name: segment.name,
      expanded: false,
      internalId: segment.internalId ?? segmentidx,
      rerender: segment.rerender ? !segment.rerender : false,
      rules: segment?.rules
        ? mapRuleEngineApiDtoToRuleEngineState(segment.rules)
        : { ruleGroups: [], matchAllRuleGroups: true },
      rulesBeforeEdit: segment?.rules
        ? mapRuleEngineApiDtoToRuleEngineState(segment.rules)
        : { ruleGroups: [], matchAllRuleGroups: true },
    }));
  };

  function groupSegmentsByAbTestId(segments: any[], reverseOrder: boolean = false): any[] {
    const grouped = new Map<number | 'noGroup', any[]>();

    for (const segment of segments) {
      const key = segment.abTestGroupId ?? 'noGroup';
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)!.push(segment);
    }

    // Flatten the grouped values into a single array
    const result = Array.from(grouped.values()).flat();
    if (reverseOrder) {
      return result.reverse();
    }
    return result;
  }

  const invalidateGroupsCache = useCallback((groupIds: number[]) => {
    groupIds.forEach((id) => loadedGroupsRef.current.delete(id));
  }, []);

  const refetchGroupShippings = useCallback(
    async (groupId: number) => {
      try {
        const response = await checkoutApi.get(`/admin/v2/shippings/${groupId}/methods`);

        setCheckouts((existingCheckouts) =>
          existingCheckouts.map((checkout) =>
            checkout.id === groupId ? { ...checkout, checkoutShippings: response.data || [] } : checkout,
          ),
        );

        loadedGroupsRef.current.add(groupId);
      } catch (error) {
        console.error(`Failed to refresh shippings for group ${groupId}:`, error);
        showToast({
          message: `Failed to refresh delivery methods. Please try again.`,
          mode: 'danger',
        });
      }
    },
    [checkoutApi, showToast],
  );

  return {
    checkouts,
    checkoutSettings,
    activeCheckoutId,
    activeCheckout,
    setActiveCheckoutId,
    checkoutActions,
    segmentActions,
    apiErrorNotification,
    setApiErrorNotification,
    fetchData,
    invalidateGroupsCache,
    refetchGroupShippings,
    fetchAllCheckoutShippings,
    isLoadingAllGroups,
  };
};

function getErrorTitle(message: string): string {
  if (message.includes('overlaps with')) {
    return 'Overlapping segments';
  }

  if (message.includes('one live checkout with no segments')) {
    return 'Overlapping default checkout';
  }

  return '';
}

export default useCheckouts;
